import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Code,
  Plus,
  Pencil,
  Trash2,
  Save,
  RefreshCw,
  Play,
  Pause,
  Copy,
  CheckCircle,
  XCircle,
  Percent,
  Truck,
  CreditCard,
  Tag,
  Gift,
  ShoppingCart,
  Zap,
  FileCode,
  Eye,
  Download,
  Package,
  User,
  Crown,
  Layers,
  Sparkles,
  MapPin,
  Scale,
  Banknote,
  Calculator,
  AlertCircle,
  Users,
  Star,
  Cake,
  DollarSign,
  Search
} from "lucide-react";

const API = "/api";

const SCRIPT_TEMPLATES = [
  // ==================== İNDİRİM SCRIPTLERİ ====================
  {
    id: "percentage_discount",
    name: "Yüzde İndirim",
    icon: Percent,
    category: "İndirim",
    description: "Sepet veya ürünlere yüzde indirim uygula",
    template: `// Yüzde İndirim Script
// %10 indirim uygula
const discountPercent = 10;

Input.cart.line_items.forEach((line) => {
  const discount = line.line_price.cents * (discountPercent / 100);
  line.change_line_price(
    line.line_price.subtract(Money.new(discount, line.line_price.currency)),
    "Özel İndirim %10"
  );
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "bogo",
    name: "1 Al 1 Bedava (BOGO)",
    icon: Gift,
    category: "İndirim",
    description: "BOGO kampanyası - ikinci ürün bedava",
    template: `// 1 Al 1 Bedava (En Ucuzu Bedava)
const eligibleItems = Input.cart.line_items.filter(
  (item) => item.quantity >= 2
);

eligibleItems.forEach((line) => {
  const freeItemsCount = Math.floor(line.quantity / 2);
  const unitPrice = line.line_price.cents / line.quantity;
  const discount = unitPrice * freeItemsCount;
  
  line.change_line_price(
    line.line_price.subtract(Money.new(discount, line.line_price.currency)),
    "1 Al 1 Bedava"
  );
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "tiered_discount",
    name: "Kademeli İndirim",
    icon: Tag,
    category: "İndirim",
    description: "Sepet tutarına göre kademeli indirim",
    template: `// Kademeli İndirim
// 200+ TL = %5, 500+ TL = %10, 1000+ TL = %15
const tiers = [
  { min: 100000, discount: 15 }, // 1000 TL
  { min: 50000, discount: 10 },  // 500 TL
  { min: 20000, discount: 5 }    // 200 TL
];

const subtotal = Input.cart.subtotal_price.cents;
const tier = tiers.find(t => subtotal >= t.min);

if (tier) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (tier.discount / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      \`%\${tier.discount} Kademeli İndirim\`
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "bundle_discount",
    name: "Paket İndirimi",
    icon: ShoppingCart,
    category: "İndirim",
    description: "Belirli ürün kombinasyonlarına indirim",
    template: `// Paket İndirimi
// A ve B ürünü birlikte alınca %15 indirim
const BUNDLE_PRODUCTS = ["URUN_A_SKU", "URUN_B_SKU"];
const BUNDLE_DISCOUNT = 15;

const productCodes = Input.cart.line_items.map(item => item.variant.sku);
const hasAllBundleItems = BUNDLE_PRODUCTS.every(sku => productCodes.includes(sku));

if (hasAllBundleItems) {
  Input.cart.line_items.forEach((line) => {
    if (BUNDLE_PRODUCTS.includes(line.variant.sku)) {
      const discount = line.line_price.cents * (BUNDLE_DISCOUNT / 100);
      line.change_line_price(
        line.line_price.subtract(Money.new(discount, line.line_price.currency)),
        "Paket İndirimi %15"
      );
    }
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "quantity_discount",
    name: "Adet Bazlı İndirim",
    icon: Package,
    category: "İndirim",
    description: "Belirli adetten fazla alımda indirim",
    template: `// Adet Bazlı İndirim
// 3+ adet alımda %10, 5+ adet alımda %20, 10+ adet alımda %30
const QUANTITY_TIERS = [
  { minQty: 10, discount: 30 },
  { minQty: 5, discount: 20 },
  { minQty: 3, discount: 10 }
];

Input.cart.line_items.forEach((line) => {
  const tier = QUANTITY_TIERS.find(t => line.quantity >= t.minQty);
  
  if (tier) {
    const discount = line.line_price.cents * (tier.discount / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      \`\${tier.discount}% Toplu Alım İndirimi\`
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "buy_x_get_y",
    name: "X Al Y Öde",
    icon: Gift,
    category: "İndirim",
    description: "3 al 2 öde, 5 al 4 öde gibi kampanyalar",
    template: `// X Al Y Öde Kampanyası
// 3 Al 2 Öde (en ucuzu bedava)
const BUY_QUANTITY = 3;
const PAY_QUANTITY = 2;

Input.cart.line_items.forEach((line) => {
  if (line.quantity >= BUY_QUANTITY) {
    const freeCount = Math.floor(line.quantity / BUY_QUANTITY) * (BUY_QUANTITY - PAY_QUANTITY);
    const unitPrice = line.line_price.cents / line.quantity;
    const discount = unitPrice * freeCount;
    
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      \`\${BUY_QUANTITY} Al \${PAY_QUANTITY} Öde\`
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "fixed_amount_discount",
    name: "Sabit Tutar İndirim",
    icon: DollarSign,
    category: "İndirim",
    description: "Sepetten sabit tutar indir (örn: 50 TL)",
    template: `// Sabit Tutar İndirim
// Sepet toplamından 50 TL indir
const DISCOUNT_AMOUNT = 5000; // 50 TL (cents)
const MIN_CART_VALUE = 20000; // Minimum 200 TL sepet

const subtotal = Input.cart.subtotal_price.cents;

if (subtotal >= MIN_CART_VALUE) {
  const discountRatio = DISCOUNT_AMOUNT / subtotal;
  
  Input.cart.line_items.forEach((line) => {
    const lineDiscount = Math.floor(line.line_price.cents * discountRatio);
    line.change_line_price(
      line.line_price.subtract(Money.new(lineDiscount, line.line_price.currency)),
      "50 TL İndirim"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "first_order_discount",
    name: "İlk Sipariş İndirimi",
    icon: User,
    category: "İndirim",
    description: "Yeni müşterilere özel indirim",
    template: `// İlk Sipariş İndirimi
// Yeni müşterilere %15 indirim
const FIRST_ORDER_DISCOUNT = 15;

const isFirstOrder = Input.customer?.orders_count === 0 || !Input.customer;

if (isFirstOrder) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (FIRST_ORDER_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "İlk Sipariş İndirimi %15"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "vip_discount",
    name: "VIP Müşteri İndirimi",
    icon: Crown,
    category: "İndirim",
    description: "Belirli etiketli müşterilere özel indirim",
    template: `// VIP Müşteri İndirimi
// "vip" veya "premium" etiketli müşterilere %20 indirim
const VIP_TAGS = ["vip", "premium", "gold"];
const VIP_DISCOUNT = 20;

const customerTags = Input.customer?.tags || [];
const isVIP = VIP_TAGS.some(tag => customerTags.includes(tag));

if (isVIP) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (VIP_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "VIP Müşteri İndirimi %20"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "category_discount",
    name: "Kategori İndirimi",
    icon: Layers,
    category: "İndirim",
    description: "Belirli kategorideki ürünlere indirim",
    template: `// Kategori İndirimi
// Belirli koleksiyondaki ürünlere %25 indirim
const ELIGIBLE_COLLECTIONS = ["sezon-sonu", "outlet", "indirimli"];
const CATEGORY_DISCOUNT = 25;

Input.cart.line_items.forEach((line) => {
  const productCollections = line.variant.product.collections || [];
  const hasEligibleCollection = productCollections.some(
    c => ELIGIBLE_COLLECTIONS.includes(c.handle)
  );
  
  if (hasEligibleCollection) {
    const discount = line.line_price.cents * (CATEGORY_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Kategori İndirimi %25"
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "product_tag_discount",
    name: "Etiket Bazlı İndirim",
    icon: Tag,
    category: "İndirim",
    description: "Belirli etiketli ürünlere indirim",
    template: `// Etiket Bazlı İndirim
// "sale" veya "clearance" etiketli ürünlere %30 indirim
const SALE_TAGS = ["sale", "clearance", "indirimli", "outlet"];
const TAG_DISCOUNT = 30;

Input.cart.line_items.forEach((line) => {
  const productTags = line.variant.product.tags || [];
  const hasSaleTag = SALE_TAGS.some(tag => productTags.includes(tag));
  
  if (hasSaleTag) {
    const discount = line.line_price.cents * (TAG_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "İndirimli Ürün %30"
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "spend_and_save",
    name: "Harca ve Kazan",
    icon: Sparkles,
    category: "İndirim",
    description: "Belirli tutarın üzerinde sabit indirim",
    template: `// Harca ve Kazan Kampanyası
// 300 TL üzeri 30 TL, 500 TL üzeri 75 TL, 1000 TL üzeri 200 TL indirim
const SPEND_TIERS = [
  { minSpend: 100000, saveAmount: 20000 }, // 1000 TL harca, 200 TL kazan
  { minSpend: 50000, saveAmount: 7500 },   // 500 TL harca, 75 TL kazan
  { minSpend: 30000, saveAmount: 3000 }    // 300 TL harca, 30 TL kazan
];

const subtotal = Input.cart.subtotal_price.cents;
const tier = SPEND_TIERS.find(t => subtotal >= t.minSpend);

if (tier) {
  const discountRatio = tier.saveAmount / subtotal;
  
  Input.cart.line_items.forEach((line) => {
    const lineDiscount = Math.floor(line.line_price.cents * discountRatio);
    line.change_line_price(
      line.line_price.subtract(Money.new(lineDiscount, line.line_price.currency)),
      \`\${tier.saveAmount / 100} TL Kazandınız!\`
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "gift_with_purchase",
    name: "Hediye Ürün Ekleme",
    icon: Gift,
    category: "İndirim",
    description: "Belirli tutarda alışverişe hediye ürün",
    template: `// Hediye Ürün Ekleme
// 500 TL üzeri alışverişte hediye ürünü bedava yap
const MIN_PURCHASE = 50000; // 500 TL
const GIFT_SKU = "HEDIYE-URUN-SKU";

const subtotal = Input.cart.subtotal_price.cents;

Input.cart.line_items.forEach((line) => {
  if (line.variant.sku === GIFT_SKU && subtotal >= MIN_PURCHASE) {
    line.change_line_price(
      Money.zero(line.line_price.currency),
      "Hediye Ürün"
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  // ==================== KARGO SCRIPTLERİ ====================
  {
    id: "free_shipping",
    name: "Ücretsiz Kargo",
    icon: Truck,
    category: "Kargo",
    description: "Belirli tutarın üzerinde ücretsiz kargo",
    template: `// Ücretsiz Kargo
// 500 TL üzeri ücretsiz kargo
const MIN_AMOUNT = 50000; // cents (500 TL)

if (Input.cart.subtotal_price.cents >= MIN_AMOUNT) {
  Input.shipping_rates.forEach((rate) => {
    rate.change_price(Money.zero(rate.price.currency), "Ücretsiz Kargo");
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "discounted_shipping",
    name: "İndirimli Kargo",
    icon: Truck,
    category: "Kargo",
    description: "Kargo ücretine indirim uygula",
    template: `// İndirimli Kargo
// 300 TL üzeri yarı fiyatına kargo
const MIN_AMOUNT = 30000; // 300 TL
const SHIPPING_DISCOUNT = 50; // %50 indirim

if (Input.cart.subtotal_price.cents >= MIN_AMOUNT) {
  Input.shipping_rates.forEach((rate) => {
    const discount = rate.price.cents * (SHIPPING_DISCOUNT / 100);
    rate.change_price(
      rate.price.subtract(Money.new(discount, rate.price.currency)),
      "%50 Kargo İndirimi"
    );
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "vip_free_shipping",
    name: "VIP Ücretsiz Kargo",
    icon: Crown,
    category: "Kargo",
    description: "VIP müşterilere her zaman ücretsiz kargo",
    template: `// VIP Ücretsiz Kargo
// VIP etiketli müşterilere ücretsiz kargo
const VIP_TAGS = ["vip", "premium", "gold"];

const customerTags = Input.customer?.tags || [];
const isVIP = VIP_TAGS.some(tag => customerTags.includes(tag));

if (isVIP) {
  Input.shipping_rates.forEach((rate) => {
    rate.change_price(Money.zero(rate.price.currency), "VIP Ücretsiz Kargo");
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "flat_rate_shipping",
    name: "Sabit Kargo Ücreti",
    icon: Truck,
    category: "Kargo",
    description: "Tüm siparişlere sabit kargo ücreti",
    template: `// Sabit Kargo Ücreti
// Tüm siparişler için 29.90 TL kargo
const FLAT_RATE = 2990; // 29.90 TL

Input.shipping_rates.forEach((rate) => {
  rate.change_price(
    Money.new(FLAT_RATE, rate.price.currency),
    "Sabit Kargo Ücreti"
  );
});

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "hide_shipping_options",
    name: "Kargo Seçeneği Gizle",
    icon: Eye,
    category: "Kargo",
    description: "Belirli kargo seçeneklerini gizle",
    template: `// Kargo Seçeneği Gizle
// 100 TL altı siparişlerde express kargoyu gizle
const MIN_FOR_EXPRESS = 10000; // 100 TL
const EXPRESS_NAMES = ["Express", "Hızlı Kargo", "Aynı Gün"];

const subtotal = Input.cart.subtotal_price.cents;

if (subtotal < MIN_FOR_EXPRESS) {
  Input.shipping_rates = Input.shipping_rates.filter((rate) => {
    return !EXPRESS_NAMES.some(name => 
      rate.name.toLowerCase().includes(name.toLowerCase())
    );
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "location_shipping",
    name: "Bölgesel Kargo",
    icon: MapPin,
    category: "Kargo",
    description: "Bölgeye göre kargo ücreti",
    template: `// Bölgesel Kargo Ücreti
// İstanbul için ücretsiz, diğer iller için standart
const FREE_SHIPPING_CITIES = ["İstanbul", "Istanbul", "Ankara", "İzmir"];

const city = Input.cart.shipping_address?.city || "";
const isFreeCity = FREE_SHIPPING_CITIES.some(
  c => city.toLowerCase().includes(c.toLowerCase())
);

if (isFreeCity) {
  Input.shipping_rates.forEach((rate) => {
    rate.change_price(Money.zero(rate.price.currency), "Ücretsiz Kargo");
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  {
    id: "weight_based_shipping",
    name: "Ağırlık Bazlı Kargo",
    icon: Scale,
    category: "Kargo",
    description: "Sepet ağırlığına göre kargo ücreti",
    template: `// Ağırlık Bazlı Kargo
// 5 kg üzeri ek kargo ücreti
const WEIGHT_THRESHOLD = 5000; // 5 kg (gram)
const EXTRA_CHARGE_PER_KG = 500; // 5 TL/kg

const totalWeight = Input.cart.line_items.reduce((sum, line) => {
  return sum + (line.variant.weight || 0) * line.quantity;
}, 0);

if (totalWeight > WEIGHT_THRESHOLD) {
  const extraKg = Math.ceil((totalWeight - WEIGHT_THRESHOLD) / 1000);
  const extraCharge = extraKg * EXTRA_CHARGE_PER_KG;
  
  Input.shipping_rates.forEach((rate) => {
    rate.change_price(
      rate.price.add(Money.new(extraCharge, rate.price.currency)),
      \`+\${extraCharge / 100} TL Ağırlık Ücreti\`
    );
  });
}

Output.shipping_rates = Input.shipping_rates;`,
    type: "shipping"
  },
  // ==================== ÖDEME SCRIPTLERİ ====================
  {
    id: "payment_discount",
    name: "Ödeme Yöntemi İndirimi",
    icon: CreditCard,
    category: "Ödeme",
    description: "Belirli ödeme yöntemlerine indirim",
    template: `// Kredi Kartına Özel İndirim
// Kredi kartı ile ödemede %5 indirim
const ELIGIBLE_PAYMENT_METHODS = ["credit_card", "visa", "mastercard"];
const DISCOUNT_PERCENT = 5;

if (ELIGIBLE_PAYMENT_METHODS.includes(Input.payment_method?.type)) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (DISCOUNT_PERCENT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Kredi Kartı İndirimi %5"
    );
  });
}

Output.cart = Input.cart;`,
    type: "payment"
  },
  {
    id: "hide_payment_gateway",
    name: "Ödeme Yöntemi Gizle",
    icon: CreditCard,
    category: "Ödeme",
    description: "Belirli durumlarda ödeme yöntemini gizle",
    template: `// Ödeme Yöntemi Gizle
// 100 TL altı siparişlerde kapıda ödemeyi gizle
const MIN_FOR_COD = 10000; // 100 TL
const COD_GATEWAYS = ["Kapıda Ödeme", "Cash on Delivery", "COD"];

const subtotal = Input.cart.subtotal_price.cents;

if (subtotal < MIN_FOR_COD) {
  Input.payment_gateways = Input.payment_gateways.filter((gateway) => {
    return !COD_GATEWAYS.some(name => 
      gateway.name.toLowerCase().includes(name.toLowerCase())
    );
  });
}

Output.payment_gateways = Input.payment_gateways;`,
    type: "payment"
  },
  {
    id: "reorder_payment_gateways",
    name: "Ödeme Sıralaması",
    icon: CreditCard,
    category: "Ödeme",
    description: "Ödeme yöntemlerini yeniden sırala",
    template: `// Ödeme Yöntemi Sıralama
// Kredi kartını en üste, havaleyi en alta al
const GATEWAY_ORDER = {
  "Kredi Kartı": 1,
  "Credit Card": 1,
  "PayPal": 2,
  "Apple Pay": 3,
  "Google Pay": 4,
  "Havale/EFT": 10,
  "Kapıda Ödeme": 11
};

Input.payment_gateways.sort((a, b) => {
  const orderA = GATEWAY_ORDER[a.name] || 5;
  const orderB = GATEWAY_ORDER[b.name] || 5;
  return orderA - orderB;
});

Output.payment_gateways = Input.payment_gateways;`,
    type: "payment"
  },
  {
    id: "cod_extra_fee",
    name: "Kapıda Ödeme Ücreti",
    icon: Banknote,
    category: "Ödeme",
    description: "Kapıda ödemede ek ücret",
    template: `// Kapıda Ödeme Ek Ücreti
// Kapıda ödemede 15 TL ek ücret
const COD_FEE = 1500; // 15 TL
const COD_METHODS = ["kapida_odeme", "cod", "cash_on_delivery"];

if (COD_METHODS.includes(Input.payment_method?.type)) {
  const firstLine = Input.cart.line_items[0];
  if (firstLine) {
    firstLine.change_line_price(
      firstLine.line_price.add(Money.new(COD_FEE, firstLine.line_price.currency)),
      "Kapıda Ödeme Ücreti +15 TL"
    );
  }
}

Output.cart = Input.cart;`,
    type: "payment"
  },
  {
    id: "installment_discount",
    name: "Tek Çekim İndirimi",
    icon: Calculator,
    category: "Ödeme",
    description: "Tek çekim ödemede indirim",
    template: `// Tek Çekim İndirimi
// Tek çekim ödemede %3 indirim
const SINGLE_PAYMENT_DISCOUNT = 3;

const isSinglePayment = Input.payment_method?.installments === 1;

if (isSinglePayment) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (SINGLE_PAYMENT_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Tek Çekim İndirimi %3"
    );
  });
}

Output.cart = Input.cart;`,
    type: "payment"
  },
  // ==================== ÖZEL SCRIPTLERİ ====================
  {
    id: "flash_sale",
    name: "Flash Sale",
    icon: Zap,
    category: "Özel",
    description: "Sınırlı süreli indirim kampanyası",
    template: `// Flash Sale - Sınırlı Süreli İndirim
// Belirli saatler arasında %40 indirim
const FLASH_DISCOUNT = 40;
const START_HOUR = 10; // Sabah 10
const END_HOUR = 14;   // Öğlen 2

const now = new Date();
const currentHour = now.getHours();
const isFlashSaleActive = currentHour >= START_HOUR && currentHour < END_HOUR;

if (isFlashSaleActive) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (FLASH_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Flash Sale %40"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "birthday_discount",
    name: "Doğum Günü İndirimi",
    icon: Cake,
    category: "Özel",
    description: "Doğum günü ayında özel indirim",
    template: `// Doğum Günü İndirimi
// Doğum günü ayında %15 indirim
const BIRTHDAY_DISCOUNT = 15;

const customerBirthMonth = Input.customer?.metafields?.custom?.birth_month;
const currentMonth = new Date().getMonth() + 1; // 1-12

if (customerBirthMonth && parseInt(customerBirthMonth) === currentMonth) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (BIRTHDAY_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Doğum Günü İndirimi %15"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "loyalty_points",
    name: "Sadakat Puanı",
    icon: Star,
    category: "Özel",
    description: "Sadakat puanlarını indirim olarak kullan",
    template: `// Sadakat Puanı Kullanımı
// Her 10 puan = 1 TL indirim
const POINTS_PER_TL = 10;
const loyaltyPoints = Input.customer?.metafields?.loyalty?.points || 0;

if (loyaltyPoints > 0) {
  const discountTL = Math.floor(loyaltyPoints / POINTS_PER_TL);
  const discountCents = discountTL * 100;
  const subtotal = Input.cart.subtotal_price.cents;
  
  const maxDiscount = Math.floor(subtotal * 0.5);
  const actualDiscount = Math.min(discountCents, maxDiscount);
  
  if (actualDiscount > 0) {
    const discountRatio = actualDiscount / subtotal;
    Input.cart.line_items.forEach((line) => {
      const lineDiscount = Math.floor(line.line_price.cents * discountRatio);
      line.change_line_price(
        line.line_price.subtract(Money.new(lineDiscount, line.line_price.currency)),
        \`\${actualDiscount / 100} TL Puan Kullanıldı\`
      );
    });
  }
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "minimum_order_fee",
    name: "Minimum Sipariş Ücreti",
    icon: AlertCircle,
    category: "Özel",
    description: "Minimum sipariş altında ek ücret",
    template: `// Minimum Sipariş Ücreti
// 100 TL altı siparişlere 20 TL ek ücret
const MIN_ORDER = 10000; // 100 TL
const SMALL_ORDER_FEE = 2000; // 20 TL

const subtotal = Input.cart.subtotal_price.cents;

if (subtotal < MIN_ORDER) {
  const firstLine = Input.cart.line_items[0];
  if (firstLine) {
    firstLine.change_line_price(
      firstLine.line_price.add(Money.new(SMALL_ORDER_FEE, firstLine.line_price.currency)),
      "Küçük Sipariş Ücreti +20 TL"
    );
  }
}

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "exclude_gift_cards",
    name: "Hediye Kartı Hariç",
    icon: CreditCard,
    category: "Özel",
    description: "Hediye kartlarını indirimden hariç tut",
    template: `// Hediye Kartlarını İndirimden Hariç Tut
// %10 indirim uygula ama hediye kartlarına değil
const DISCOUNT_PERCENT = 10;

Input.cart.line_items.forEach((line) => {
  if (!line.variant.product.gift_card) {
    const discount = line.line_price.cents * (DISCOUNT_PERCENT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Özel İndirim %10"
    );
  }
});

Output.cart = Input.cart;`,
    type: "discount"
  },
  {
    id: "referral_discount",
    name: "Referans İndirimi",
    icon: Users,
    category: "Özel",
    description: "Referans koduyla gelen müşterilere indirim",
    template: `// Referans Kodu İndirimi
// Referans koduyla gelen müşterilere %10 indirim
const REFERRAL_DISCOUNT = 10;
const REFERRAL_TAG = "referred";

const customerTags = Input.customer?.tags || [];
const isReferred = customerTags.includes(REFERRAL_TAG);

if (isReferred) {
  Input.cart.line_items.forEach((line) => {
    const discount = line.line_price.cents * (REFERRAL_DISCOUNT / 100);
    line.change_line_price(
      line.line_price.subtract(Money.new(discount, line.line_price.currency)),
      "Referans İndirimi %10"
    );
  });
}

Output.cart = Input.cart;`,
    type: "discount"
  }
];

// Kategorilere göre grup
const CATEGORIES = ["İndirim", "Kargo", "Ödeme", "Özel"];

export default function ShopifyScriptsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [scripts, setScripts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [scriptForm, setScriptForm] = useState({
    name: "",
    description: "",
    type: "discount",
    code: "",
    isActive: true,
    priority: 1,
    conditions: {
      minCartValue: 0,
      maxCartValue: 0,
      customerTags: [],
      productTags: [],
      startDate: "",
      endDate: ""
    }
  });

  useEffect(() => {
    fetchScripts();
  }, [token]);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shopify-scripts?token=${token}`);
      setScripts(response.data.scripts || []);
    } catch (error) {
      console.error("Scripts fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingScript(null);
    setScriptForm({
      name: "",
      description: "",
      type: "discount",
      code: "",
      isActive: true,
      priority: 1,
      conditions: { minCartValue: 0, maxCartValue: 0, customerTags: [], productTags: [], startDate: "", endDate: "" }
    });
    setDialogOpen(true);
  };

  const openEditDialog = (script) => {
    setEditingScript(script);
    setScriptForm({
      name: script.name || "",
      description: script.description || "",
      type: script.type || "discount",
      code: script.code || "",
      isActive: script.isActive !== false,
      priority: script.priority || 1,
      conditions: script.conditions || { minCartValue: 0, maxCartValue: 0, customerTags: [], productTags: [], startDate: "", endDate: "" }
    });
    setDialogOpen(true);
  };

  const handleUseTemplate = (template) => {
    setScriptForm({
      ...scriptForm,
      name: template.name,
      description: template.description,
      type: template.type,
      code: template.template
    });
    setTemplateDialogOpen(false);
    setDialogOpen(true);
  };

  const saveScript = async () => {
    if (!scriptForm.name) {
      toast.error("Script adı zorunludur");
      return;
    }
    if (!scriptForm.code) {
      toast.error("Script kodu zorunludur");
      return;
    }

    setSaving(true);
    try {
      if (editingScript) {
        await axios.put(`${API}/shopify-scripts/${editingScript.id}?token=${token}`, scriptForm);
        toast.success("Script güncellendi");
      } else {
        await axios.post(`${API}/shopify-scripts?token=${token}`, scriptForm);
        toast.success("Script oluşturuldu");
      }
      setDialogOpen(false);
      fetchScripts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteScript = async (scriptId) => {
    if (!confirm("Bu script'i silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/shopify-scripts/${scriptId}?token=${token}`);
      toast.success("Script silindi");
      fetchScripts();
    } catch (error) {
      toast.error("Script silinemedi");
    }
  };

  const toggleScript = async (scriptId, isActive) => {
    try {
      await axios.put(`${API}/shopify-scripts/${scriptId}/toggle?token=${token}`, { isActive });
      toast.success(isActive ? "Script aktifleştirildi" : "Script devre dışı bırakıldı");
      fetchScripts();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const deployScript = async (scriptId) => {
    try {
      await axios.post(`${API}/shopify-scripts/${scriptId}/deploy?token=${token}`);
      toast.success("Script Shopify'a yüklendi");
      fetchScripts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yükleme başarısız");
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "discount": return <Percent className="w-4 h-4" />;
      case "shipping": return <Truck className="w-4 h-4" />;
      case "payment": return <CreditCard className="w-4 h-4" />;
      default: return <Code className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "discount": return "İndirim";
      case "shipping": return "Kargo";
      case "payment": return "Ödeme";
      default: return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "discount": return "bg-emerald-500";
      case "shipping": return "bg-blue-500";
      case "payment": return "bg-purple-500";
      default: return "bg-slate-500";
    }
  };

  const filteredTemplates = SCRIPT_TEMPLATES.filter(t => {
    const matchCategory = selectedCategory === "all" || t.category === selectedCategory;
    const matchSearch = !searchTerm || 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="space-y-6 fade-in" data-testid="shopify-scripts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Shopify Scripts</h1>
          <p className="text-slate-500 mt-1">Özel indirim, kargo ve ödeme kuralları oluşturun</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTemplateDialogOpen(true)}>
            <FileCode className="w-4 h-4 mr-2" />
            Şablonlar ({SCRIPT_TEMPLATES.length})
          </Button>
          <Button onClick={openAddDialog} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Script
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Code className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{scripts.length}</p>
                <p className="text-xs text-slate-500">Toplam Script</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Percent className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{scripts.filter(s => s.type === "discount").length}</p>
                <p className="text-xs text-slate-500">İndirim Script</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{scripts.filter(s => s.type === "shipping").length}</p>
                <p className="text-xs text-slate-500">Kargo Script</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{scripts.filter(s => s.isActive).length}</p>
                <p className="text-xs text-slate-500">Aktif Script</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scripts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-indigo-500" />
            Script Listesi
          </CardTitle>
          <CardDescription>Shopify mağazanızda çalışacak özel kurallar</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : scripts.length === 0 ? (
            <div className="text-center py-12">
              <Code className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Henüz script oluşturulmadı</p>
              <div className="flex justify-center gap-2 mt-4">
                <Button onClick={() => setTemplateDialogOpen(true)} variant="outline">
                  <FileCode className="w-4 h-4 mr-2" />
                  {SCRIPT_TEMPLATES.length} Şablondan Seç
                </Button>
                <Button onClick={openAddDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Boş Script
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Script Adı</TableHead>
                  <TableHead>Tür</TableHead>
                  <TableHead className="text-center">Öncelik</TableHead>
                  <TableHead className="text-center">Durum</TableHead>
                  <TableHead className="text-center">Deployed</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scripts.map((script) => (
                  <TableRow key={script.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{script.name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">{script.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getTypeBadgeColor(script.type)} flex items-center gap-1 w-fit`}>
                        {getTypeIcon(script.type)}
                        {getTypeLabel(script.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{script.priority}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch checked={script.isActive} onCheckedChange={(checked) => toggleScript(script.id, checked)} />
                    </TableCell>
                    <TableCell className="text-center">
                      {script.deployedAt ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-300 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditDialog(script)}>
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        {!script.deployedAt && (
                          <Button size="icon" variant="ghost" onClick={() => deployScript(script.id)}>
                            <Zap className="w-4 h-4 text-amber-500" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => deleteScript(script.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Script Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScript ? "Script Düzenle" : "Yeni Script Oluştur"}</DialogTitle>
            <DialogDescription>Shopify Checkout Scripts veya Shopify Functions için kod yazın</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scriptName">Script Adı *</Label>
                <Input id="scriptName" value={scriptForm.name} onChange={(e) => setScriptForm({...scriptForm, name: e.target.value})} placeholder="Örn: Yılbaşı İndirimi" />
              </div>
              <div className="space-y-2">
                <Label>Script Türü</Label>
                <Select value={scriptForm.type} onValueChange={(v) => setScriptForm({...scriptForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">İndirim Script</SelectItem>
                    <SelectItem value="shipping">Kargo Script</SelectItem>
                    <SelectItem value="payment">Ödeme Script</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Input id="description" value={scriptForm.description} onChange={(e) => setScriptForm({...scriptForm, description: e.target.value})} placeholder="Script'in ne yaptığını açıklayın" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Script Kodu *</Label>
                <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(scriptForm.code).then(() => toast.success("Kopyalandı"))}>
                  <Copy className="w-4 h-4 mr-1" />Kopyala
                </Button>
              </div>
              <Textarea value={scriptForm.code} onChange={(e) => setScriptForm({...scriptForm, code: e.target.value})} placeholder="// Script kodunuzu buraya yazın..." className="font-mono text-sm h-[300px]" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Select value={scriptForm.priority.toString()} onValueChange={(v) => setScriptForm({...scriptForm, priority: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - En Yüksek</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5 - En Düşük</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Minimum Sepet (₺)</Label>
                <Input type="number" value={scriptForm.conditions.minCartValue} onChange={(e) => setScriptForm({...scriptForm, conditions: {...scriptForm.conditions, minCartValue: parseFloat(e.target.value) || 0}})} />
              </div>
              <div className="space-y-2">
                <Label>Maksimum Sepet (₺)</Label>
                <Input type="number" value={scriptForm.conditions.maxCartValue} onChange={(e) => setScriptForm({...scriptForm, conditions: {...scriptForm.conditions, maxCartValue: parseFloat(e.target.value) || 0}})} />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Aktif</Label>
                <p className="text-xs text-slate-500">Script'i etkinleştir</p>
              </div>
              <Switch checked={scriptForm.isActive} onCheckedChange={(v) => setScriptForm({...scriptForm, isActive: v})} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={saveScript} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {editingScript ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Script Şablonları ({SCRIPT_TEMPLATES.length})</DialogTitle>
            <DialogDescription>Hazır şablonlardan birini seçerek hızlıca script oluşturun</DialogDescription>
          </DialogHeader>

          {/* Filters */}
          <div className="flex gap-4 py-2 border-b">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Şablon ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü ({SCRIPT_TEMPLATES.length})</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat} ({SCRIPT_TEMPLATES.filter(t => t.category === cat).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto py-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredTemplates.map((template) => (
                <Card key={template.id} className="cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all" onClick={() => handleUseTemplate(template)}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                        template.type === "discount" ? "bg-emerald-100" :
                        template.type === "shipping" ? "bg-blue-100" : "bg-purple-100"
                      }`}>
                        <template.icon className={`w-4 h-4 ${
                          template.type === "discount" ? "text-emerald-600" :
                          template.type === "shipping" ? "text-blue-600" : "text-purple-600"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{template.name}</h4>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{template.description}</p>
                        <div className="flex gap-1 mt-2">
                          <Badge className={`text-xs ${getTypeBadgeColor(template.type)}`}>{getTypeLabel(template.type)}</Badge>
                          <Badge variant="outline" className="text-xs">{template.category}</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {filteredTemplates.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Şablon bulunamadı</p>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
